import dayjs from 'dayjs';
import { Box, Button, DateInput, Keyboard, Layer, Select, Text } from 'grommet';
import { Book } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { TScriptId } from '@talon/web/lib/api';
import { useProjectStore, useSessionStore } from '@talon/web/src/context';
import { DateString, FormErrors } from '@talon/web/src/types';

export interface ICreateSessionModalProps {
  addSession: (date: DateString, scriptId?: TScriptId) => Promise<void>;
  setShowModal: (isVisible: boolean) => void;
}

export const CreateSessionModal = observer(({ addSession, setShowModal }: ICreateSessionModalProps) => {
  /** Context **/
  const sessionStore = useSessionStore();
  const projectStore = useProjectStore();

  /** State **/
  // .toISOString() (not .format(), which converts to local time) keeps this a UTC-safe
  // date-only string - the "date" column round-trips through the API as UTC midnight, so
  // formatting via local time can roll the day back or forward depending on the viewer's
  // timezone offset.
  const [date, setDate] = useState(
    sessionStore.getLastSessionDate()
      ? dayjs(sessionStore.getLastSessionDate()).add(1, 'day').toISOString().slice(0, 10)
      : ''
  );
  const [scriptId, setScriptId] = useState<TScriptId>('');

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const scripts = projectStore.currentProject?.scripts.map((s) => ({ name: s.name, id: s.id })) ?? [];

  /** Methods **/
  const createSession = () => {
    if (sessionStore.getSessionDates().find((d) => dayjs(d).isSame(date))) {
      setFormErrors({ ...formErrors, date: 'A Session already exists for this date.' });
      return;
    }

    addSession(date, scriptId);
    setShowModal(false);
  };

  /** Render **/
  return (
    <Layer
      onEsc={() => setShowModal(false)}
      onClickOutside={() => setShowModal(false)}
      position="center"
      modal={true}
      margin="large"
    >
      <Box
        direction="column"
        align="start"
        pad="small"
        justify="around"
        width="medium"
        height="medium"
        background="light-1"
        round="small"
      >
        <Box>
          <Box direction="row" pad="small" gap="small" align="center" fill="horizontal">
            <Box width="20%">
              <Text>Date:</Text>
            </Box>
            <Box fill="horizontal" gap="xsmall">
              <Keyboard onEnter={createSession}>
                <DateInput
                  format="mm/dd/yyyy"
                  value={date}
                  calendarProps={{ disabled: sessionStore.getSessionDates() }}
                  onChange={({ value }) => setDate(typeof value === 'object' ? value[0] : value)}
                />
              </Keyboard>
              {formErrors.date && (
                <Box fill="horizontal" align="end">
                  <Text size="xsmall" color="red">
                    {formErrors.date}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box direction="row" gap="small" pad="small" align="center" fill="horizontal">
          <Box width="20%">
            <Text>Script:</Text>
          </Box>
          <Box fill="horizontal">
            <Select
              id="select-script"
              name="script"
              placeholder="Choose..."
              options={scripts}
              labelKey="name"
              valueKey="id"
              onChange={({ value }) => setScriptId(value.id)}
              icon={<Book />}
            />
          </Box>
        </Box>
        <Button pad="small" margin="small" hoverIndicator onClick={() => createSession()} label="Add Session" />
      </Box>
    </Layer>
  );
});
